import { getMCPMonitorDistribution } from "@/api/endpoints/searches";
import { SOURCE_CODE_TO_LABEL } from "@/constants";
import { useI18n } from "@/contexts/i18nContext";
import { cn } from "@/lib/utils";
import { SearchFilterV4WithDates } from "@/types/SearchFilter";
import {
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetProps,
  WidgetStat,
  WidgetTitle,
} from "@components/ui/widget";
import {
  getMCPMonitorFiltersFromSearchFilterV4,
  getTopChannelType,
} from "@components/widgets/utils";
import { i18n } from "@lingui/core";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";

export default function TopChannelTypeWidget({
  searchUuid,
  filters,
  className,
  ...rest
}: WidgetProps & {
  searchUuid: Uuid;
  filters: SearchFilterV4WithDates;
}) {
  const { formatPercent } = useI18n();

  // Server state
  const {
    data: channelTypeDistribution,
    isLoading: isChannelTypeDistributionLoading,
  } = useQuery({
    queryKey: [searchUuid, "channel-type-distribution", filters],
    queryFn: () =>
      getMCPMonitorDistribution(searchUuid, {
        ...getMCPMonitorFiltersFromSearchFilterV4(filters),
        metric: "mention_count",
      }),
  });

  const shares = channelTypeDistribution?.ok
    ? channelTypeDistribution.data.shares
    : undefined;

  const topChannelType = shares ? getTopChannelType(shares) : undefined;

  return (
    <Widget className={cn("gap-0.5", className)} {...rest}>
      <WidgetHeader isLoading={isChannelTypeDistributionLoading}>
        <WidgetTitle>
          <Trans>Största medietyp</Trans>
        </WidgetTitle>
        {topChannelType ? (
          <WidgetStat>
            {i18n._(SOURCE_CODE_TO_LABEL[topChannelType])}
          </WidgetStat>
        ) : null}
      </WidgetHeader>
      <WidgetContent>
        {topChannelType && shares ? (
          <p className="text-right text-xs text-balance">
            {formatPercent(shares[topChannelType], 0)}{" "}
            <span className="text-muted-foreground">
              {" "}
              <Trans>av träffarna</Trans>
            </span>
          </p>
        ) : null}
      </WidgetContent>
    </Widget>
  );
}
